import classNames from 'classnames';
import React, {
    ReactElement,
    useState,
    useEffect,
    useMemo,
    Dispatch,
    SetStateAction,
    Fragment,
} from 'react';

import { selectInputDisplayName } from './SelectInput.qa';

export interface ISelectOption {
    name: string;
    value: any;
}

export interface ISelectInputProps {
    onChange: (option: ISelectOption) => void;
    disabled?: boolean;
    label: string;
    'data-testid'?: string;
    className?: string;
    options: ISelectOption[];
    selectedValue?: any;
    selectedIndex?: number;
    placeholder?: string;
    name?: string;
}

const emptyOption: ISelectOption = {
    name: '',
    value: '',
};

function renderOption (option: ISelectOption, index: number, dataTestId: string): ReactElement {
    return (
        <option
            key={index}
            value={index}
            data-testid={!!dataTestId && `${dataTestId}-select-option-${index}`}
        >
            {option.name}
        </option>
    );
}

function renderOptions (options: ISelectOption[], dataTestId: string): ReactElement {
    return (
        <Fragment>
            {options.map((option: ISelectOption, index: number): ReactElement => {
                return renderOption(option, index, dataTestId);
            })}
        </Fragment>
    );
}

function getDefaultSelectedOption (
    options: ISelectOption[],
    defaultSelectedValue: any | undefined,
    defaultSelectedIndex: number | undefined,
): ISelectOption {
    let initialOption: ISelectOption;

    if (defaultSelectedValue) {
        initialOption = options.find((opt: ISelectOption): boolean => {
            return opt.value === defaultSelectedValue;
        }) || options[0];
    } else {
        if (
            defaultSelectedIndex === undefined ||
            defaultSelectedIndex < 0 ||
            defaultSelectedIndex > options.length
        ) {
            initialOption = options[0];
        } else {
            // increase the defaultIndex value by 1 because of the addition of emptyOption to options
            initialOption = options[defaultSelectedIndex + 1];
        }
    }

    return initialOption;
}

export function OzSelectInput (props: ISelectInputProps): ReactElement {
    const {
        disabled = false,
        label,
        'data-testid': dataTestId = '',
        className = '',
        options,
        selectedValue,
        selectedIndex,
        placeholder,
        onChange,
        name = '',
    }: ISelectInputProps = props;

    emptyOption.name = placeholder || label;

    const [optionsWithDefault, setOptionsWithDefault]: [ISelectOption[], Dispatch<SetStateAction<ISelectOption[]>>] =
    useState<ISelectOption[]>([emptyOption, ...options]);

    useEffect((): void => {
        setOptionsWithDefault([emptyOption, ...options]);
    }, [options]);

    const initialOption: ISelectOption = useMemo((): ISelectOption => {
        return getDefaultSelectedOption(optionsWithDefault, selectedValue, selectedIndex);
    }, [optionsWithDefault, selectedValue, selectedIndex]);

    const [selectedOption, setSelectedOption]: [ISelectOption, Dispatch<SetStateAction<ISelectOption>>] = useState<ISelectOption>(initialOption);

    useEffect((): void => {
        onChange(selectedOption);
    }, [selectedOption, onChange]);

    useEffect((): void => {
        const newSelectedOption: ISelectOption = getDefaultSelectedOption(optionsWithDefault, selectedValue, selectedIndex);

        setSelectedOption(newSelectedOption);
    }, [selectedValue, optionsWithDefault, selectedIndex]);

    const wrapperClassName: string = classNames({
        'w-full h-7 px-2 flex flex-row justify-between items-center relative border rounded bg-white outline-none focus:outline-none ': true,
        [className]: !!className,
        'text-black-30 border outline-none cursor-default': disabled,
        'text-black-75': !disabled,
    });

    const labelClassName: string = classNames({
        'w-9/12 top-1/2 block type-subtitle2 !font-normal text-left font-primary capitalize truncate opacity-100 leading-4 absolute transition-opacity ease-in-out duration-500': true,
        'flex opacity-00': (selectedOption.name === emptyOption.name && selectedOption.value === emptyOption.value),
        'text-black-30': disabled,
    });

    const emptyOptionClassName: string = classNames({
        'truncate capitalize text-left': true,
        'text-black-30': disabled,
    });

    const valueClassName: string = classNames({
        'mt-1 text-secondary-contrast truncate capitalize text-left type-body1 font-normal': true,
        '!text-black-30': disabled,
    });

    function handleSelectChange (event: React.ChangeEvent<HTMLSelectElement>): void {
        const selectedOptionIndex: number = parseInt(event.target.value);
        const currentSelectedOption = optionsWithDefault[selectedOptionIndex];

        setSelectedOption(currentSelectedOption);
    }

    return (
        <button
            className={wrapperClassName}
            disabled={disabled}
            data-testid={dataTestId}
        >
            <div className='w-85/100'>
                <label className={labelClassName}>{label}</label>
                {(selectedOption.name === emptyOption.name && selectedOption.value === emptyOption.value) ?
                    <div className={emptyOptionClassName}>{label}</div>
                    :
                    <div className={valueClassName}>{selectedOption.name}</div>
                }
            </div>
            <div className='h-7/10 w-4 border-l flex items-center justify-end'>
                <span className='inline-block transform scale-x-125'>
                    &#x25BC;
                </span>
            </div>

            <select
                className='w-full h-full type-body1 absolute left-0 opacity-00 font-normal'
                defaultValue={selectedOption.value}
                onChange={handleSelectChange}
                disabled={disabled}
                data-testid={!!dataTestId && `${dataTestId}-select`}
                name={name}
            >
                {renderOptions(optionsWithDefault, dataTestId)}
            </select>
        </button>
    );
}

OzSelectInput.displayName = selectInputDisplayName;
